import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

class CartDrawerComponent extends DialogComponent {
  #summaryThreshold = 0.5;
  #historyAbortController = null;
  #notification = null;
  #manualOpenRequested = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#hideNotification);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.addEventListener('resize', this.#handleResize);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#hideNotification);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.removeEventListener('resize', this.#handleResize);

    this.#manualOpenRequested = false;
    this.#historyAbortController?.abort();
    this.#notification?.remove();
    this.#notification = null;
  }

  #isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  showDialog() {
    if (this.refs.dialog?.open) return;

    if (this.#isMobile() && !this.#manualOpenRequested) {
      return;
    }

    this.#manualOpenRequested = false;
    this.#hideNotification();

    super.showDialog();
  }

#handleCartAdd = (event) => {
  if (event.detail?.data?.didError) return;

  this.open();
  this.#announceCartCount(event.detail?.resource?.item_count);
};

  #showNotification() {
    if (!this.#notification) {
      const notice = document.createElement('div');

      notice.setAttribute('popover', 'manual');
      notice.setAttribute('aria-label', 'Added to bag');
      notice.hidden = true;

      notice.style.cssText = `
        position: fixed;
        inset: 12px 12px auto;
        margin: 0;
        width: auto;
        max-width: none;
        box-sizing: border-box;
        padding: 14px;
        background: #f3f3f3;
        color: #111;
        border: 1px solid #ddd;
        border-radius: 0;
        box-shadow: 0 4px 20px #0002;
        z-index: 2147483647;
        font-family: var(--font-body--family, inherit);
      `;

      const row = document.createElement('div');

      row.style.cssText = `
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 10px;
      `;

      const message = document.createElement('span');

      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');

      message.style.cssText = `
        font-size: 14px;
        font-weight: 600;
        line-height: 1.4;
      `;

      const dismiss = document.createElement('button');

      dismiss.type = 'button';
      dismiss.textContent = '×';
      dismiss.setAttribute('aria-label', 'Dismiss added to bag message');

      dismiss.style.cssText = `
        display: grid;
        place-items: center;
        flex-shrink: 0;
        width: 44px;
        height: 44px;
        margin: -8px -8px -8px 0;
        padding: 0;
        border: 0;
        background: transparent;
        color: #111;
        font: 26px/1 sans-serif;
        cursor: pointer;
      `;

      dismiss.addEventListener('click', this.#hideNotification);
      row.append(message, dismiss);

      const actions = document.createElement('div');

      actions.style.cssText = `
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
      `;

      const buttonStyle = `
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 44px;
        box-sizing: border-box;
        padding: 10px 8px;
        border: 1px solid #111;
        border-radius: 0;
        font-family: inherit;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.4;
        letter-spacing: .06em;
        text-align: center;
        text-decoration: none;
        cursor: pointer;
      `;

      const viewBag = document.createElement('button');

      viewBag.type = 'button';
      viewBag.textContent = 'VIEW BAG';
      viewBag.style.cssText = buttonStyle + 'background:#fff;color:#111;';
      viewBag.addEventListener('click', () => this.open());

      const checkout = document.createElement('a');

      checkout.textContent = 'CHECKOUT';
      checkout.href = (window.Shopify?.routes?.root || '/') + 'checkout';
      checkout.style.cssText = buttonStyle + 'background:#111;color:#fff;';

      actions.append(viewBag, checkout);
      notice.append(row, actions);
      document.body.append(notice);

      this.#notification = notice;
    }

    const notice = this.#notification;
    notice.hidden = false;

    if (typeof notice.showPopover === 'function') {
      if (!notice.matches(':popover-open')) {
        notice.showPopover();
      }
    } else {
      notice.removeAttribute('popover');
    }

    const message = notice.querySelector('[role="status"]');

    if (message) {
      message.textContent = 'Product added to bag!';
    }
  }

  #hideNotification = () => {
    const notice = this.#notification;
    if (!notice) return;

    if (
      typeof notice.hidePopover === 'function' &&
      notice.matches(':popover-open')
    ) {
      notice.hidePopover();
    }

    notice.hidden = true;
  };

  #handleResize = () => {
    if (!this.#isMobile()) {
      this.#hideNotification();
    }
  };

  #announceCartCount(cartCount) {
    const liveRegion = this.refs.liveRegion;

    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) {
      return;
    }

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  #handleHistoryOpen = () => {
    if (!this.#isMobile()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController?.abort();
    this.#historyAbortController = new AbortController();

    window.addEventListener('popstate', this.#handlePopState, {
      signal: this.#historyAbortController.signal,
    });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty(
        '--dialog-drawer-closing-animation',
        'none'
      );

      await this.closeDialog();

      this.refs.dialog.style.removeProperty(
        '--dialog-drawer-closing-animation'
      );
    }
  };

  open() {
    this.#manualOpenRequested = true;

    try {
      this.showDialog();
    } finally {
      this.#manualOpenRequested = false;
    }

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent =
        document.querySelector('shopify-payment-terms')?.shadowRoot;

      const cta = installmentsContent?.querySelector('#shopify-installments-cta');

      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = this.refs;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}